import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconLoader2,
} from "@tabler/icons-react";
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";

interface TeamsWithInstalledApp {
  id: string;
  name: string;
}

interface TeamsConnectionStatusResponse {
  is_admin_consent_granted: boolean;
}

type FetchState<T> =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "loaded"; data: T };

export type InstalledTeamsFetchState = FetchState<TeamsWithInstalledApp[]>;

function useTeamsApiFetch<T>(
  url: string | null,
  reloadNonce: number,
): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({ kind: "loading" });

  useEffect(() => {
    if (url === null) {
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    void (async () => {
      try {
        const axios = await getAxiosInstanceWithAuth();
        const { data } = await axios.get<T>(url);
        if (!cancelled) {
          setState({ kind: "loaded", data });
        }
      } catch {
        if (!cancelled) {
          setState({ kind: "error" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, reloadNonce]);

  return state;
}

/**
 * The installed-teams check fans out to the Microsoft Graph API per team and
 * can take seconds, so it is fetched once by the integrations section and
 * shared by the overview table's status cell and the detail page's connection
 * card — navigating between the two reuses the result instead of re-running
 * the fan-out. Pass null while no Teams integration is connected.
 */
export function useTeamsInstalledTeams(integrationId: string | null): {
  teamsWithInstalledAppFetch: InstalledTeamsFetchState;
  retryFetch: () => void;
} {
  const [reloadNonce, setReloadNonce] = useState(0);
  const installedTeamsFetch = useTeamsApiFetch<TeamsWithInstalledApp[]>(
    integrationId === null ? null : `/teams/${integrationId}/installed-teams`,
    reloadNonce,
  );
  const retryFetch = useCallback(
    () => setReloadNonce((nonce) => nonce + 1),
    [],
  );
  return {
    teamsWithInstalledAppFetch: installedTeamsFetch,
    retryFetch,
  };
}

/**
 * The integrations table's status cell for Teams. For other providers a row's
 * existence alone means "Connected", but Teams only delivers once the app is
 * installed in at least one team — so this cell reflects live install state
 * instead of a bare "Connected".
 */
export const TeamsIntegrationTableStatus: FC<{
  installedTeamsFetch: InstalledTeamsFetchState;
}> = ({ installedTeamsFetch }) => {
  if (installedTeamsFetch.kind === "loading") {
    return (
      <IconLoader2
        aria-hidden
        className="size-4 animate-spin text-muted-foreground"
      />
    );
  }
  if (installedTeamsFetch.kind === "error") {
    return (
      <span className="text-xs font-medium text-amber-500">
        Couldn&apos;t verify
      </span>
    );
  }
  if (installedTeamsFetch.data.length > 0) {
    return <span className="text-xs font-medium text-success">Connected</span>;
  }
  return (
    <span className="text-xs font-medium text-amber-500">
      App not installed
    </span>
  );
};

const StatusRow: FC<{
  state: "success" | "warning" | "loading";
  label: string;
  children?: ReactNode;
}> = ({ state, label, children }) => (
  <div className="flex items-start gap-2">
    {state === "success" ? (
      <IconCircleCheck
        aria-hidden
        className="mt-0.5 size-4 shrink-0 text-success"
      />
    ) : state === "warning" ? (
      <IconAlertTriangle
        aria-hidden
        className="mt-0.5 size-4 shrink-0 text-amber-500"
      />
    ) : (
      <IconLoader2
        aria-hidden
        className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground"
      />
    )}
    <div className="min-w-0">
      <div className="text-sm font-medium text-foreground">{label}</div>
      {children}
    </div>
  </div>
);

/**
 * The Teams connection is two-part, unlike Slack's single OAuth handshake:
 * admin consent records which Microsoft 365 tenant (the customer's Microsoft
 * org) this company sends to, and separately a Teams admin must add the
 * Alpharun app to the teams it should deliver to — installation is per team,
 * and only installed teams can receive channel posts (or DMs to their
 * members). This card makes a half-connected tenant legible and tells the
 * admin what's still missing.
 *
 * Consent state is fetched here; the installed-teams fetch is shared with the
 * overview table (see useTeamsInstalledTeams), and its row loads
 * independently instead of holding up the card.
 */
export const TeamsConnectionStatus: FC<{
  integrationId: string;
  installedTeamsFetch: InstalledTeamsFetchState;
  onRetryInstalledTeams: () => void;
}> = ({ integrationId, installedTeamsFetch, onRetryInstalledTeams }) => {
  // Bumped by the retry button to re-run the fetch effect.
  const [statusReloadNonce, setStatusReloadNonce] = useState(0);

  const statusFetch = useTeamsApiFetch<TeamsConnectionStatusResponse>(
    `/teams/${integrationId}/status`,
    statusReloadNonce,
  );

  if (statusFetch.kind === "loading") {
    return <Loader style={LoaderStyle.SPINNER} />;
  }

  if (statusFetch.kind === "error") {
    return (
      <div className="flex max-w-3xl flex-col items-start gap-3 rounded-lg border border-border-light bg-card p-4">
        <div className="flex items-start gap-2">
          <IconAlertTriangle
            aria-hidden
            className="mt-0.5 size-4 shrink-0 text-amber-500"
          />
          <div className="min-w-0">
            <div className="text-sm font-medium text-foreground">
              Couldn&apos;t load connection status
            </div>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Something went wrong checking your Teams connection.
            </p>
          </div>
        </div>
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.SMALL}
          onClick={() => {
            setStatusReloadNonce((nonce) => nonce + 1);
            onRetryInstalledTeams();
          }}
        >
          Try again
        </Button>
      </div>
    );
  }

  const { is_admin_consent_granted: isAdminConsentGranted } = statusFetch.data;

  const renderInstalledTeamsRow = () => {
    if (installedTeamsFetch.kind === "loading") {
      return (
        <StatusRow state="loading" label="Alpharun app installed in Teams">
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Checking which teams have the app installed…
          </p>
        </StatusRow>
      );
    }

    if (installedTeamsFetch.kind === "error") {
      return (
        <StatusRow state="warning" label="Alpharun app installed in Teams">
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Couldn&apos;t check which teams have the app installed.
          </p>
          <div className="mt-2">
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.SMALL}
              onClick={onRetryInstalledTeams}
            >
              Try again
            </Button>
          </div>
        </StatusRow>
      );
    }

    const installedTeams = installedTeamsFetch.data;
    if (installedTeams.length === 0) {
      return (
        <StatusRow state="warning" label="Alpharun app installed in Teams">
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Not installed in any team. Ask your Teams admin to add the Alpharun
            app to your teams.
          </p>
        </StatusRow>
      );
    }

    return (
      <StatusRow state="success" label="Alpharun app installed in Teams">
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Alpharun can post to channels and message members in these teams:
        </p>
        <ul className="mt-1 list-disc pl-4 text-xs leading-5 text-foreground">
          {installedTeams.map((team) => (
            <li key={team.id}>{team.name}</li>
          ))}
        </ul>
      </StatusRow>
    );
  };

  return (
    <div className="flex max-w-3xl flex-col gap-3 rounded-lg border border-border-light bg-card p-4">
      <h3 className="text-sm font-semibold text-foreground">
        Connection status
      </h3>
      <StatusRow
        state={isAdminConsentGranted ? "success" : "warning"}
        label="Admin consent granted"
      >
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {isAdminConsentGranted ? (
            <>
              A Microsoft 365 administrator has approved Alpharun&apos;s access
              to your organization&apos;s directory and Teams.
            </>
          ) : (
            <>
              A Microsoft 365 administrator needs to approve Alpharun&apos;s
              access to your organization&apos;s directory and Teams. Use the
              Connect button above.
            </>
          )}
        </p>
      </StatusRow>
      {renderInstalledTeamsRow()}
    </div>
  );
};
